import SofiaSansFont from '../../platformAssets/runtime/fonts/SofiaSans-VariableFont_wght.ttf';
import i18n from '../i18n/i18n';
import { isStandaloneDisplay } from '../services/pwa/installPrompt';

let paymentWindow: Window | null = null;

const escapeHtml = (text: string) => text.replace(/[&<>"']/g, char => `&#${char.charCodeAt(0)};`);

/**
 * The page the payment tab shows until the checkout loads: the app's red panel with the gold rim
 * (the Daily Missions / checkout modal chrome) on the app's dark red backdrop.
 */
function loadingPageHtml(): string {
	// The tab starts as about:blank, so the font URL is made absolute against the app's page.
	const fontUrl = new URL(SofiaSansFont, window.location.href).href;
	const title = escapeHtml(
		i18n.t('T_SITE_PAYMENT_OPENING', { defaultValue: 'Opening secure payment...' }),
	);
	const hint = escapeHtml(
		i18n.t('T_SITE_PAYMENT_CLOSE_TAB_HINT', {
			defaultValue: 'You can close this tab at any time',
		}),
	);

	return `<!DOCTYPE html><html lang="${escapeHtml(i18n.language || 'en')}"><head><meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>${title}</title>
<style>
@font-face{font-family:'Sofia Sans';src:url('${fontUrl}') format('truetype');font-weight:100 900;font-display:swap}
@keyframes spin{to{transform:rotate(360deg)}}
html,body{height:100%}
body{margin:0;display:flex;align-items:center;justify-content:center;padding:16px;box-sizing:border-box;
background:radial-gradient(ellipse at 50% 35%,#8A0C03 0%,#4A0602 45%,#1A0603 100%);
font-family:'Sofia Sans',system-ui,sans-serif;color:#fff}
.panel{width:100%;max-width:340px;box-sizing:border-box;padding:32px 24px;text-align:center;border-radius:16px;
border:1.5px solid #FFF908;background:linear-gradient(180deg,#770700 0%,#320400 80%,#320400 100%);
box-shadow:0 0 16px rgba(255,184,0,.45)}
.spinner{width:44px;height:44px;margin:0 auto 20px;border-radius:50%;border:4px solid rgba(255,197,0,.25);
border-top-color:#FFC500;animation:spin 1s linear infinite;filter:drop-shadow(0 0 6px rgba(255,114,0,.6))}
.title{font-size:20px;line-height:24px;font-weight:800;color:#FFE275;text-shadow:0 0 6px rgba(255,114,0,.6);margin-bottom:8px}
.hint{font-size:14px;line-height:18px;font-weight:500;color:rgba(255,255,255,.75)}
</style></head><body><div class="panel"><div class="spinner"></div><div class="title">${title}</div><div class="hint">${hint}</div></div></body></html>`;
}

function writeLoadingPage(win: Window): void {
	try {
		win.document.open();
		win.document.write(loadingPageHtml());
		win.document.close();
	} catch (e) {
		console.warn('[webPaymentWindow] Failed to prepare payment window:', e);
	}
}

export function openWebPaymentWindow(): void {
	if (typeof window === 'undefined') return;

	closeWebPaymentWindow();

	// The installed web app (PWA) opens popups in a separate in-app browser with its own
	// storage on iOS, so the result page could never hand the outcome back. Leave the window
	// unopened: the callers then fall back to the checkout in this very page, and the
	// provider returns to `/?payment=...&deposit_id=...`, which the boot already handles.
	if (isStandaloneDisplay()) {
		paymentWindow = null;
		return;
	}

	try {
		if (typeof localStorage !== 'undefined') {
			localStorage.removeItem('web_payment_result');
		}
	} catch (e) {
		console.warn('[webPaymentWindow] Failed to clear stale payment result:', e);
	}

	try {
		const win = window.open('', '_blank');
		if (win) {
			paymentWindow = win;
			writeLoadingPage(win);
		} else {
			paymentWindow = null;
		}
	} catch (e) {
		console.warn('[webPaymentWindow] window.open failed:', e);
		paymentWindow = null;
	}
}

export function getWebPaymentWindow(): Window | null {
	if (paymentWindow && paymentWindow.closed) {
		paymentWindow = null;
	}
	return paymentWindow;
}

export function navigateWebPaymentWindow(url: string): boolean {
	const win = getWebPaymentWindow();
	if (!win) return false;
	try {
		win.location.href = url;
		return true;
	} catch (e) {
		console.warn('[webPaymentWindow] Failed to navigate payment window:', e);
		return false;
	}
}

export function closeWebPaymentWindow(): void {
	try {
		paymentWindow?.close?.();
	} catch (e) {
		console.warn('[webPaymentWindow] Failed to close payment window:', e);
	}
	paymentWindow = null;
}
